// Search API client.
//
// Talks to OUR middleware, which calls Wizzy on the server side. It used to call
// Wizzy (api.wizsearch.in) straight from the browser, which meant shipping the
// x-store-id and x-api-key in the DOM and on every request. The middleware holds
// those now, serves the same endpoints the mobile app uses, and falls back to
// Shopify by itself when Wizzy is down (see `source` below).
//
// The base URL is read from the [data-search-api-config] element rendered by
// snippet 'search-api-config', which resolves it from the search_api_environment
// theme setting. No credentials are involved any more.
//
// Every method resolves to a `payload` object in the SAME shape the surfaces have
// always consumed (result, facets, total, pages, filters.searchedKey…) or throws
// on a network / HTTP / application error. The middleware answers in its own
// mapped shape; normaliseListing below translates it back, which is what keeps
// wizzy-collection.js and wizzy-search-drawer.js out of this migration entirely.
//
// WHICH ENDPOINT WHEN. Wizzy attributes analytics per endpoint, so the split is
// not interchangeable — using the wrong one loses the metric rather than
// erroring:
//   typing in the search bar  -> autocomplete()  (suggestions AND top products;
//                                                 one call, not two)
//   a search is submitted     -> search()        (the only call that records a
//                                                 search hit; mints searchedKey)
//   everything after that     -> filter()        (pagination, sort, facets, and
//                                                 every collection-page request)
// Each filter() call must also carry the `type` matching its surface (see TYPE)
// and, once one is held, the `searchedKey` (see getSearchedKey). `facets` must name
// the groups wanted: only the keys asked for come back, so there is no "all".
//
// Usage:
//   const payload = await window.WizzyApi.filter(
//     {
//       categories: ["men"],
//       page: 1,
//       productsCount: 12,
//       facets: [{ key: "sizes" }, { key: "product_Colour" }],
//       type: window.WizzyApi.TYPE.COLLECTION,
//       searchedKey: window.WizzyApi.getSearchedKey("collection:men"),
//     },
//     { signal }
//   );
//   const payload = await window.WizzyApi.search(
//     { q: "shirt", productsCount: 12, facets: [{ key: "sizes" }] },
//     { signal }
//   );
//   const payload = await window.WizzyApi.autocomplete(
//     { q: "shir", productsCount: 6 },
//     { signal }
//   );

// Production middleware. Only a fallback for the value the config element carries:
// the environment is a theme setting so a preview theme can point at UAT without a
// deploy. Production is the safe default for a page that somehow renders no config.
const DEFAULT_BASE_URL = "https://th-prod-ep-bwfweterf5bjffdb.a01.azurefd.net";

// Session-scoped outage flag, shared by every Wizzy-backed surface (PLP grid,
// search results page, search drawer). One failure puts them all straight into
// their native fallback for the rest of the session, so a shopper waits out the
// timeout once rather than on every navigation.
const DOWN_KEY = "wizzy-api-down";

// How long that flag stands before a surface is willing to try Wizzy again.
//
// It used to have no expiry, which made a transient outage permanent: one failure
// pinned the whole tab to the native fallback until the shopper closed it. A
// refresh does not help — sessionStorage survives reloads — and shoppers cannot
// clear it themselves. That is exactly what happened while the Wizzy subscription
// was lapsed and every call 402'd: sessions poisoned then stayed on native search
// long after the API came back.
//
// Two minutes is the balance — long enough that a genuinely dead API is not
// re-tried on every keystroke (each retry costs FALLBACK_TIMEOUT_MS of waiting),
// short enough that a shopper recovers inside one visit.
const DOWN_TTL_MS = 120000;

// Wizzy returns a `searchedKey` on every search/filter response and expects it
// echoed on every following filter request for the same search or collection.
// It is how a result set stays coherent once Wizzy has spell-corrected the query
// or resolved it to several entities ("nike sports shoes" -> brand + category):
// without it, page 2 and each facet change are re-resolved from scratch and can
// disagree with page 1.
//
// It has to outlive a navigation — the drawer submits the search, the results
// page is what paginates and filters — so sessionStorage rather than a field.
// Per-tab, so two tabs on different listings cannot overwrite each other.
//
// One slot is enough (a shopper is on one listing at a time); the scope stored
// beside it ("search:shirt", "collection:men-jeans") is what makes a key
// belonging to a different listing ignorable instead of silently reused.
const SEARCHED_KEY_STORAGE = "wizzy:searched-key";

// Just the base URL now. It cannot fail the way the credential read could — a
// missing config element falls back to production rather than disabling search,
// because there is no longer a wrong-store hazard in guessing: the middleware is
// per-environment and serves this store either way.
const baseUrl = () =>
  document.querySelector("[data-search-api-config]")?.dataset.baseUrl || DEFAULT_BASE_URL;

// Every middleware response is wrapped as { success, error, data }. `success: false`
// carries { code, message } and is a real failure however the HTTP status came out, so
// it is checked separately from response.ok — the direct-Wizzy client had the same
// shape of problem (HTTP 200 over an application error) and the same answer. Throwing
// is what engages each caller's fallback: the search drawer reveals native predictive
// search, the PLP reveals the Liquid grid.
const unwrap = (path, body) => {
  if (body?.success === false || body?.error) {
    const { code, message } = body?.error ?? {};
    const detail = `${code ? ` (${code})` : ""}${message ? `: ${message}` : ""}`;
    throw new Error(`Search API ${path} failed${detail}`);
  }
  return body?.data ?? null;
};

// POST a plain JSON body. Nothing is stringified field-by-field the way Wizzy's
// form-encoded API required — the middleware takes objects and arrays as they are.
const postJson = async (path, body, { signal } = {}) => {
  const response = await fetch(`${baseUrl()}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });

  // 429 (Wizzy rate limit) and 503 (search down AND its fallback failed) land here, which
  // is what puts the calling surface onto its own native fallback.
  if (!response.ok) {
    throw new Error(`Search API ${path} failed with ${response.status}`);
  }

  return unwrap(path, await response.json());
};

const get = async (path, { signal } = {}) => {
  const response = await fetch(`${baseUrl()}${path}`, { method: "GET", signal });
  if (!response.ok) throw new Error(`Search API ${path} failed with ${response.status}`);
  return unwrap(path, await response.json());
};

// A product in the shape the card renderers read.
//
// The middleware returns Wizzy's fields under Shopify-ish names and adds enriched ones
// on top. Renaming them back here keeps the translation in one place instead of spread
// across two renderers (wizzy-collection.js and wizzy-search-drawer.js both clone
// snippet 'product-card-template' and read these names). Everything not listed is
// already identical on both sides — notably `swatches`, `attributes` and `sizes`, which
// is why card swatches and checkColorFacet need no change at all.
const normaliseProduct = (product) => ({
  ...product,
  // The bare variant id Wizzy returned. The middleware re-labels it `id: "wizzy-<n>"` and
  // moves the number to `wizzyId`. Nothing in the theme reads either today — the card takes
  // its wishlist key from `groupId` — but this is the field that identified a row before,
  // so it is handed back unchanged rather than left as a shape the old contract never had.
  id: product.wizzyId ?? product.id,
  name: product.title ?? "",
  mainImage: product.featuredImage?.url ?? "",
  // The one field that is NOT byte-identical to what Wizzy returned. Wizzy sent the
  // gallery in catalogue order with `mainImage` sitting somewhere inside it; the
  // middleware hoists the featured image to index 0 and dedupes it out of its old slot,
  // and the original position is not recoverable from the response. Inert for this
  // theme: both renderers read `product.mainImage || product.images?.[0]`, and
  // `mainImage` is identical on both sides — so index 0 is only ever the fallback for a
  // row with no main image, where the featured image is the better answer anyway. The
  // hover cycling does not read this at all; it refetches from Shopify's own
  // /products/{handle}.js (see product-card-behaviour.js).
  images: (product.images ?? []).map((image) => image?.url).filter(Boolean),
  // Keeps the ?variant= that ProductCardBehaviour.variantUrl expects to find.
  url: product.wizzyUrl ?? "",
  // The compare-at price. `originalPrice` is 0 when Wizzy has no value, and the card
  // only strikes through when price > sellingPrice, so 0 reads as "no MRP" correctly.
  price: product.originalPrice ?? 0,
  brand: product.vendor ?? "",
  // An array, as Wizzy sent it — the middleware only renamed the field. Taking [0] here
  // looked right and was wrong: the old contract was a list and the diff against
  // ?raw=true caught it.
  sku: product.skus ?? [],
});

// A listing response (filter + search) in the shape the surfaces read.
//
// `source: "shopify"` means the middleware's own Shopify fallback answered. That is a
// SUCCESS, not an outage: facets are limited and there is no searchedKey, but there are
// real products to render, so it must not reach markDown(). Logged because an otherwise
// healthy-looking page with thin facets is hard to explain in QA without it.
const normaliseListing = (data) => {
  if (!data) return null;
  if (data.source === "shopify") {
    console.info("[SearchApi] served by the Shopify fallback — facets will be limited");
  }

  return {
    result: (data.products ?? []).map(normaliseProduct),
    total: data.total ?? 0,
    pages: data.pages ?? 0,
    hasExactMatch: data.hasExactMatch ?? false,
    facets: data.facets ?? [],
    collectionTitle: data.collectionTitle,
    source: data.source,
    // searchedKeyFrom() reads payload.filters.searchedKey, which is where Wizzy put it.
    // Rebuilt rather than changed at the call sites so the session helpers either side
    // of it stay as they are.
    filters: { searchedKey: data.searchedKey ?? "" },
  };
};

window.WizzyApi = {
  // Attribute id for the product's custom.display_tag metafield ("Limited
  // Edition"), which drives the card's overlay badge. Wizzy suffixes
  // metafield-derived attributes with _custom.
  DISPLAY_TAG_KEY: "product_display_tag_custom",

  // Wizzy attribute id holding the colour option's values, used for both the PLP card
  // swatches and the drawer's colour facet.
  //
  // This TRACKS THE SHOPIFY OPTION NAME: Wizzy derives the id as `product_<Option Name>`,
  // so it changes every time the option is renamed. The catalogue has renamed it three
  // times — "Color" -> "Brand Colour" -> "Basic Colour" -> "Colour" — and the first two
  // silently emptied the colour facet and every card swatch until this constant caught up.
  // Note the British spelling: `product_Color` returns nothing.
  //
  // A previous fix moved this to the position-based `product_option1` on the theory that
  // Wizzy does not build name-based attributes for custom-named options. That was a
  // misreading of an index in flux: right after a rename Wizzy serves the old id for a
  // while and the new one reads empty, so whichever key is tested first looks like the
  // working one. Once it finished re-indexing, `product_option1` disappeared outright and
  // the name-based id carried everything. Verified against /products/filter:
  //
  //   product_Basic Colour   facet 16 values   swatch 105 values   <- before the 2026-09-14 rename
  //   product_Basic Color    absent            swatch 0
  //   product_option1        absent            swatch 0
  //   colors                 facet 0           swatch 0            <- built-in, never populated
  //
  // 2026-09-14: the option was renamed to "Colour" on all 806 products and this constant
  // moved to `product_Colour` in the same change. Re-verify against /products/filter once
  // Wizzy has re-indexed.
  //
  // So after any option rename, expect BOTH to be briefly wrong and re-check once Wizzy has
  // re-indexed. checkColorFacet in wizzy-collection.js turns the next rename into a console
  // warning naming the key Wizzy actually returned, rather than silently blank swatches.
  COLOR_SWATCH_KEY: "product_Colour",

  // A product's distinct colourways, for the card swatch dots. Populated only when the
  // request asked for them via `swatch: [{ key: COLOR_SWATCH_KEY }]`; each entry nests one
  // level deeper than it looks — [{ value: ["Classic Pink"] }] — and repeats per variant,
  // so dedupe while preserving variant order.
  //
  // Shared for the same global-scope reason as attributeValue below: the PLP and the
  // search drawer both need it and cannot each declare it.
  cardColors(product) {
    const raw = product?.swatches?.[this.COLOR_SWATCH_KEY]?.values ?? [];
    const distinct = [];
    for (const entry of raw) {
      const value = Array.isArray(entry?.value) ? entry.value[0] : entry?.value;
      if (value && !distinct.includes(value)) distinct.push(value);
    }
    return distinct;
  },

  // First value of a Wizzy product attribute, or "" when absent. Values nest one
  // level deeper than the attribute: [{ value: ["Limited Edition"] }].
  // Lives here rather than in each card renderer: wizzy-collection.js and
  // wizzy-search-drawer.js are classic scripts sharing one global scope, so a
  // top-level const in both is a redeclaration SyntaxError.
  attributeValue(product, key) {
    const attribute = product?.attributes?.find((item) => item.id === key);
    for (const entry of attribute?.values ?? []) {
      const value = entry?.value?.[0];
      if (value) return String(value).trim();
    }
    return "";
  },

  // How long a surface waits on its first Wizzy call before giving up and
  // revealing its native fallback. Shared for the same global-scope reason as
  // attributeValue above.
  FALLBACK_TIMEOUT_MS: 6000,

  // Wizzy's `type` for /products/filter: what the request is attributed to in
  // their analytics. Every filter call must send the value for the surface it
  // came from — an omitted `type` is not an error, it just silently reports as
  // DEFAULT, so a collection page shows up as search traffic.
  TYPE: {
    SEARCH_RESULTS: "DEFAULT",
    COLLECTION: "CATEGORY_PAGE",
    AUTOCOMPLETE_DEFAULT: "AUTOCOMPLETE_DEFAULT",
  },

  // The stored searchedKey for `scope`, or "" when there is none — or when the
  // stored one belongs to a different listing, which would return results for
  // the wrong query. "" is the correct value for a first request: the caller
  // simply omits the field.
  getSearchedKey(scope) {
    try {
      const stored = JSON.parse(sessionStorage.getItem(SEARCHED_KEY_STORAGE) ?? "{}");
      return stored.scope === scope ? (stored.key ?? "") : "";
    } catch {
      return ""; // private mode or a corrupt entry — treat as a first request
    }
  },

  setSearchedKey(scope, key) {
    if (!key) return;
    try {
      sessionStorage.setItem(SEARCHED_KEY_STORAGE, JSON.stringify({ scope, key }));
    } catch {
      /* storage unavailable — every request just looks like a first one */
    }
  },

  // Call when a new search is submitted. The key resets per search, so dropping
  // it is what makes the results page mint a fresh one via search().
  clearSearchedKey() {
    try {
      sessionStorage.removeItem(SEARCHED_KEY_STORAGE);
    } catch {
      /* ignore */
    }
  },

  // searchedKey out of a search()/filter() payload. It arrives inside
  // `payload.filters` — the config set Wizzy echoes back for building the next
  // request — and also as a `wizzy-searchedKey` response header. The body is
  // used because reading the header would need Access-Control-Expose-Headers.
  searchedKeyFrom(payload) {
    return payload?.filters?.searchedKey ?? "";
  },

  isDown() {
    try {
      // A legacy "1" (written before the flag carried a timestamp) has no `at`,
      // so it reads as expired — which is what lets already-poisoned sessions
      // heal on their next page load rather than staying stuck.
      const { at } = JSON.parse(sessionStorage.getItem(DOWN_KEY) ?? "{}") ?? {};
      if (typeof at !== "number") return false;
      return Date.now() - at < DOWN_TTL_MS;
    } catch {
      return false; // private mode or a bad entry — let the timeout decide
    }
  },

  markDown() {
    try {
      sessionStorage.setItem(DOWN_KEY, JSON.stringify({ at: Date.now() }));
    } catch {
      /* storage unavailable — the caller still shows its fallback this time */
    }
  },

  // Collection listings, and every request after the first on the search results
  // page. Everything — filter fields, page/sort/facets, `type`, `searchedKey`,
  // and `q` when filtering within a search — nests inside one `filters` object.
  async filter(filters, options) {
    return normaliseListing(await postJson("/search/products/filter", { filters }, options));
  },

  // Full search for a query. This, not filter(), is what Wizzy counts as a
  // search hit, so it has to be the first request for any new query. Answers
  // with the same payload shape as filter() plus the searchedKey. Note it takes
  // no `page` and no filter values — those are filter()'s job.
  async search(params, options) {
    return normaliseListing(await postJson("/search/products/search", params, options));
  },

  // Query suggestions in `payload.others` and, when productsCount > 0, the top
  // products in `payload.products`. One keystroke should make this call and
  // nothing else: it already covers both halves of the dropdown, and Wizzy's
  // typing analytics hang off it.
  async autocomplete(params, options) {
    const data = await postJson("/search/autocomplete", params, options);
    // Re-nested to match what the drawer reads: suggestions at `others`, and products
    // inside a listing object rather than as a bare array. Wizzy's own /autocomplete
    // answered that way and the drawer still does.
    return {
      others: data?.suggestions ?? [],
      products: normaliseListing(data) ?? { result: [] },
    };
  },

  // Trending search terms (Popular Categories in the search drawer).
  trendingSearches(options) {
    return get("/search/trending", options);
  },
};
